/**
 * UpdateBanner — zeigt einen Banner wenn eine neue Version der App
 * verfuegbar ist. Erkennt das auf zwei Wegen:
 *
 * 1. Service-Worker schickt `{ type: "SW_UPDATED" }` an alle Tabs sobald
 *    eine neue SW-Version aktiviert ist (siehe public/sw.js).
 * 2. Backend-Build-Version aendert sich (wir merken uns die initial
 *    geladene Version und vergleichen sie mit der vom /health Endpoint
 *    gepollten).
 *
 * Klick auf den Banner = window.location.reload() ohne Cache.
 */
import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";

type Props = {
  backendVersion?: string;
};

export function UpdateBanner({ backendVersion }: Props) {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [initialBackendVersion, setInitialBackendVersion] = useState<string | null>(
    null,
  );

  // 1. SW-Update-Message empfangen
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMessage = (e: MessageEvent) => {
      if (e.data && e.data.type === "SW_UPDATED") {
        setUpdateAvailable(true);
      }
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => {
      navigator.serviceWorker.removeEventListener("message", onMessage);
    };
  }, []);

  // 2. Backend-Version-Mismatch
  useEffect(() => {
    if (!backendVersion) return;
    if (!initialBackendVersion) {
      setInitialBackendVersion(backendVersion);
      return;
    }
    if (backendVersion !== initialBackendVersion) {
      setUpdateAvailable(true);
    }
  }, [backendVersion, initialBackendVersion]);

  if (!updateAvailable) return null;

  const reload = () => {
    // Wenn SW aktiv: unregister damit neues Bundle garantiert geladen wird
    try {
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.getRegistrations().then((regs) => {
          regs.forEach((reg) => reg.update());
        });
      }
    } catch {
      // ignore
    }
    // Cache-Bust via Reload mit dem Param damit Browser-Cache umgangen wird
    const url = new URL(window.location.href);
    url.searchParams.set("_v", String(Date.now()));
    window.location.replace(url.toString());
  };

  return (
    <button
      onClick={reload}
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 inline-flex items-center gap-2 rounded-full bg-accent-orange text-white shadow-xl px-5 py-2.5 text-sm font-semibold hover:scale-[1.02] transition-transform"
      style={{ boxShadow: "0 8px 24px rgba(249,115,22,0.35)" }}
    >
      <RefreshCw className="size-4" />
      Neue Version verfügbar — jetzt aktualisieren
    </button>
  );
}
